import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import useAuth from '../../../hooks/useAuth';
import { emptyCart } from '../../../redux/feathers/productsSlice';
import usersService from '../../../services/users.service';
import ChangePassword from './ChangePassword';
import styles from './Profile.module.css';

const Profile = () => {
  const { loggedInUser, logOut, setLoggedInUser } = useAuth();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: loggedInUser?.firstName || '',
    lastName: loggedInUser?.lastName || '',
    email: loggedInUser?.email || '',
    phone: loggedInUser?.phone || '',
    webSite: loggedInUser?.webSite || '',
    job: loggedInUser?.job || '',
    newsletter: loggedInUser?.newsletter || false,
    address: {
      line1: loggedInUser?.address?.line1 || '',
      line2: loggedInUser?.address?.line2 || '',
      city: loggedInUser?.address?.city || '',
      state: loggedInUser?.address?.state || '',
      postalCode: loggedInUser?.address?.postalCode || '',
      country: loggedInUser?.address?.country || '',
    },
  });

  const signOut = () => {
    logOut();
    dispatch(emptyCart());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data when canceling
      setFormData({
        firstName: loggedInUser?.firstName || '',
        lastName: loggedInUser?.lastName || '',
        email: loggedInUser?.email || '',
        phone: loggedInUser?.phone || '',
        webSite: loggedInUser?.webSite || '',
        job: loggedInUser?.job || '',
        newsletter: loggedInUser?.newsletter || false,
        address: {
          line1: loggedInUser?.address?.line1 || '',
          line2: loggedInUser?.address?.line2 || '',
          city: loggedInUser?.address?.city || '',
          state: loggedInUser?.address?.state || '',
          postalCode: loggedInUser?.address?.postalCode || '',
          country: loggedInUser?.address?.country || '',
        },
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        webSite: formData.webSite,
        job: formData.job,
        newsletter: formData.newsletter,
        address: formData.address,
      };

      const updatedUser = await usersService.updateUser(loggedInUser.id, updateData);

      // Update localStorage and state
      const updatedUserData = { ...loggedInUser, ...updatedUser };
      localStorage.setItem('kacha_bazar_user', JSON.stringify(updatedUserData));
      setLoggedInUser(updatedUserData);

      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (!loggedInUser) return null;

  return (
    <section id={styles.my__profile}>
      <Container>
        <div className={styles.header}>
          <h1>My Profile</h1>
          <div className={styles.header__actions}>
            {!isEditing ? (
              <>
                <button onClick={handleEditToggle} className={styles.edit__btn}>
                  Edit Profile
                </button>
                <button onClick={signOut} className={styles.logout__btn}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <button onClick={handleEditToggle} className={styles.cancel__btn}>
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className={styles.save__btn}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.profile__content}>
          <form onSubmit={handleSubmit}>
            <h3>Personal Information</h3>
            <Row className="g-3">
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    required
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className={styles.disabled__input}
                  />
                  <small className={styles.help__text}>Email cannot be changed</small>
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="+1234567890"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="webSite">Website</label>
                  <input
                    type="url"
                    id="webSite"
                    name="webSite"
                    value={formData.webSite}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="https://example.com"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="job">Job</label>
                  <input
                    type="text"
                    id="job"
                    name="job"
                    value={formData.job}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Your job title"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="newsletter">Newsletter</label>
                  <select
                    id="newsletter"
                    name="newsletter"
                    value={formData.newsletter}
                    onChange={(e) => setFormData({ ...formData, newsletter: e.target.value === 'true' })}
                    disabled={!isEditing}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </Col>
            </Row>

            <h3 className="mt-4">Address Information</h3>
            <Row className="g-3">
              <Col md={12}>
                <div className={styles.form__group}>
                  <label htmlFor="address.line1">Address Line 1</label>
                  <input
                    type="text"
                    id="address.line1"
                    name="address.line1"
                    value={formData.address.line1}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Street address"
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className={styles.form__group}>
                  <label htmlFor="address.line2">Address Line 2</label>
                  <input
                    type="text"
                    id="address.line2"
                    name="address.line2"
                    value={formData.address.line2}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="address.city">City</label>
                  <input
                    type="text"
                    id="address.city"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="City"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="address.state">State/Province</label>
                  <input
                    type="text"
                    id="address.state"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="State or Province"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="address.postalCode">Postal Code</label>
                  <input
                    type="text"
                    id="address.postalCode"
                    name="address.postalCode"
                    value={formData.address.postalCode}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="12345"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.form__group}>
                  <label htmlFor="address.country">Country</label>
                  <input
                    type="text"
                    id="address.country"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    placeholder="Country"
                  />
                </div>
              </Col>
            </Row>
          </form>

          <div className={styles.divider}></div>
          <ChangePassword />
        </div>
      </Container>
    </section>
  );
};

export default Profile;
